/**
 * The two brands, defined once for the whole platform. Order is the order every screen
 * lists them in: the parent brand first.
 */
export const BRANDS = ['royalaura', 'talt'] as const;

export type Brand = (typeof BRANDS)[number];

/** Royal Aura is the parent brand. Every platform hostname is on it. */
export const PRIMARY_BRAND: Brand = 'royalaura';

/** Customer-facing name. Always shown in full; never abbreviated to a colour or badge. */
export const BRAND_NAME: Readonly<Record<Brand, string>> = Object.freeze({
  royalaura: 'Royal Aura',
  talt: 'Talt',
});

export const BRAND_DOMAIN: Readonly<Record<Brand, string>> = Object.freeze({
  royalaura: 'royalaura.ae',
  talt: 'talt.ae',
});

export function isBrand(value: unknown): value is Brand {
  return typeof value === 'string' && (BRANDS as readonly string[]).includes(value);
}

export function brandName(brand: Brand): string {
  return BRAND_NAME[brand];
}

/** The brand that owns an address, by domain. Undefined for an address on neither. */
export function brandOfAddress(address: string): Brand | undefined {
  const domain = address
    .slice(address.lastIndexOf('@') + 1)
    .toLowerCase()
    .trim();
  return BRANDS.find((brand) => BRAND_DOMAIN[brand] === domain);
}

/**
 * Brand serving a hostname. The parent brand when the hostname is not one of ours,
 * so a screen shown before any mailbox is known still wears a brand rather than
 * nothing — an unbranded screen is a screen with no chrome to fail safe with.
 */
export function brandForHost(hostname: string): Brand {
  const host = hostname.toLowerCase().trim();
  return (
    BRANDS.find(
      (brand) => host === BRAND_DOMAIN[brand] || host.endsWith(`.${BRAND_DOMAIN[brand]}`),
    ) ?? PRIMARY_BRAND
  );
}
